import React from "react";
import { useTranslation } from "react-i18next";

import { InternalLabelData } from "../../../models/InternalLabelData";
import { Pagination } from "../../Pagination";
import InternalLabelsListTopBar from "../ListTopBar";
import ModalAssignFacilities from "../ModalAssignFacilities";
import "./styles.scss";

interface InternalLabelsListProps {
  internalLabels: InternalLabelData[];
  onEdit: (label: InternalLabelData) => void;
  filterFormData: any;
  totalCount: number;
  onChangeFilterFormData: (filterFormData: any) => void;
  onUpdateAssignLabels?: (
    label: InternalLabelData,
    result: { addIds: string[]; deleteIds: string[] }
  ) => void;
}

const InternalLabelsList: React.FC<InternalLabelsListProps> = ({
  internalLabels,
  onEdit,
  filterFormData,
  totalCount,
  onChangeFilterFormData,
  onUpdateAssignLabels,
}) => {
  const { t: _t } = useTranslation();

  const t = (key: string, param?: any) =>
    _t(`internalLabelsManagementPage.internalLabelsList.${key}`, param);

  const [showAssignModal, setShowAssignModal] = React.useState<string | null>(
    null
  );
  const [assigningLabel, setAssigningLabel] =
    React.useState<InternalLabelData | null>(null);
  const [expandedRows, setExpandedRows] = React.useState<
    Record<string, boolean>
  >({});

  return (
    <>
      <InternalLabelsListTopBar
        filterFormData={filterFormData}
        onChangeFilterFormData={onChangeFilterFormData}
      />
      <div className="table-data table-data-grid table-data-grid table-data-grid-pc-only internal-labels-list">
        <div className="row-th">
          <div className="col-th">
            <div className="spacing">{t("name")}</div>
          </div>
          <div className="col-th">
            <div className="spacing">{t("description")}</div>
          </div>
          <div className="col-th">
            <div className="spacing">{t("created_at")}</div>
          </div>
          <div className="col-th">
            <div className="spacing">{t("locations")}</div>
          </div>
          <div className="col-th center">
            <div className="spacing">{t("actions")}</div>
          </div>
        </div>
        {internalLabels &&
          internalLabels.length > 0 &&
          internalLabels.map((label, idx) => (
            <div className="row-td" key={label.id || idx}>
              <div className="col-td">
                <div className="spacing">{label.name}</div>
              </div>
              <div className="col-td">
                <div className="spacing">{label.description || "-"}</div>
              </div>
              <div className="col-td">
                <div className="spacing">
                  {label.created_at
                    ? new Date(label.created_at).toLocaleDateString()
                    : "-"}
                </div>
              </div>
              <div className="col-td">
                <div className="spacing">
                  {label.locations_internal_labels &&
                  label.locations_internal_labels.length > 0 ? (
                    (() => {
                      const locs = label.locations_internal_labels;
                      const firstLoc = locs[0];
                      const restCount = locs.length - 1;
                      const expanded = expandedRows[label.id];
                      return (
                        <>
                          <div key={firstLoc.locations.id}>
                            • {firstLoc.locations.name}
                          </div>
                          {restCount > 0 && !expanded && (
                            <div
                              className="show-more"
                              onClick={() =>
                                setExpandedRows((r) => ({
                                  ...r,
                                  [label.id]: true,
                                }))
                              }
                            >
                              {t("show_more", { count: restCount })}
                            </div>
                          )}
                          {expanded &&
                            locs
                              .slice(1)
                              .map((item: any) => (
                                <div key={item.locations.id}>
                                  • {item.locations.name}
                                </div>
                              ))}
                        </>
                      );
                    })()
                  ) : (
                    <span>-</span>
                  )}
                </div>
              </div>
              <div className="col-td center buttons-section">
                <div className="spacing">
                  <button
                    className="btn btn-blue"
                    onClick={() => onEdit(label)}
                  >
                    {t("edit")}
                  </button>
                  <button
                    className="btn btn-blue"
                    onClick={() => {
                      setAssigningLabel(label);
                      setShowAssignModal(label.id);
                    }}
                  >
                    {t("assign_facilities", {
                      defaultValue: "Assign Facilities",
                    })}
                  </button>
                </div>
                {showAssignModal === label.id && assigningLabel && (
                  <ModalAssignFacilities
                    internalLabel={label}
                    onClose={() => {
                      setShowAssignModal(null);
                      setAssigningLabel(null);
                    }}
                    onUpdate={(result) => {
                      if (onUpdateAssignLabels)
                        onUpdateAssignLabels(label, result);
                      setShowAssignModal(null);
                      setAssigningLabel(null);
                    }}
                  />
                )}
              </div>
            </div>
          ))}
      </div>
      {internalLabels && internalLabels.length === 0 && (
        <div className="no-data">{t("no_data")}</div>
      )}
      <Pagination
        filterFormData={filterFormData}
        totalRowsNumber={totalCount}
        onChangeFilterFormData={onChangeFilterFormData}
      />
    </>
  );
};

export default InternalLabelsList;
